import { addDays, differenceInCalendarDays } from "date-fns"

/**
 * Schedule date engine -- the SINGLE source of truth for working-day math
 * across all of Schedule (Gantt, List, Calendar). A Schedule Item's
 * `duration` means WORKING days, not calendar days: moving a task doesn't
 * change how many working days it spans, only recomputes its end date
 * against the company's configured working days
 * (Settings -> Company -> Working Days, `useCompanyWorkingDays.ts`).
 *
 * Architecture: `WorkingDaysConfig` covers weekday on/off only for now.
 * Specific non-working DATES (holidays, company shutdown) are a natural
 * follow-up -- they'd extend `isWorkingDay` to also check a date against a
 * second `company_settings` key (e.g. `schedule_non_working_dates`, an
 * array of ISO dates) without changing this module's public functions or
 * any call site, since every caller already goes through `isWorkingDay`/
 * `addWorkingDays`/`countWorkingDays` rather than checking `getDay()`
 * itself.
 */

export interface WorkingDaysConfig {
  sun: boolean
  mon: boolean
  tue: boolean
  wed: boolean
  thu: boolean
  fri: boolean
  sat: boolean
}

export const DEFAULT_WORKING_DAYS: WorkingDaysConfig = {
  mon: true,
  tue: true,
  wed: true,
  thu: true,
  fri: true,
  sat: false,
  sun: false,
}

const DOW_KEYS: (keyof WorkingDaysConfig)[] = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"]

export function isWorkingDay(date: Date, config: WorkingDaysConfig = DEFAULT_WORKING_DAYS): boolean {
  return config[DOW_KEYS[date.getDay()]]
}

/**
 * The date `durationDays` working days after `start`, INCLUSIVE of `start`
 * (durationDays=1 returns `start` itself). Calendar days that aren't
 * working days are passed over without counting.
 *
 * Example (Mon-Fri working, Sat/Sun off): start=Monday, durationDays=5 ->
 * Mon,Tue,Wed,Thu,Fri -> returns Friday. start=Wednesday, durationDays=5 ->
 * Wed,Thu,Fri,(skip Sat/Sun),Mon,Tue -> returns the following Tuesday.
 */
export function addWorkingDays(start: Date, durationDays: number, config: WorkingDaysConfig = DEFAULT_WORKING_DAYS): Date {
  if (durationDays <= 1) return start
  let cursor = start
  let counted = 1
  while (counted < durationDays) {
    cursor = addDays(cursor, 1)
    if (isWorkingDay(cursor, config)) counted++
  }
  return cursor
}

/** Inverse of addWorkingDays -- working days between start/end, inclusive on both ends. */
export function countWorkingDays(start: Date, end: Date, config: WorkingDaysConfig = DEFAULT_WORKING_DAYS): number {
  if (end <= start) return 1
  let count = isWorkingDay(start, config) ? 1 : 0
  let cursor = start
  while (cursor < end) {
    cursor = addDays(cursor, 1)
    if (isWorkingDay(cursor, config)) count++
  }
  return Math.max(1, count)
}

/**
 * Mirror of addWorkingDays going BACKWARD -- the date `durationDays`
 * working days before `end`, inclusive of `end` (durationDays=1 returns
 * `end` itself). Used to back-compute a task's start from a Finish-to-
 * Finish/Start-to-Finish dependency's required end date, preserving the
 * task's own working-day duration.
 */
export function subtractWorkingDays(end: Date, durationDays: number, config: WorkingDaysConfig = DEFAULT_WORKING_DAYS): Date {
  if (durationDays <= 1) return end
  let cursor = end
  let counted = 1
  while (counted < durationDays) {
    cursor = addDays(cursor, -1)
    if (isWorkingDay(cursor, config)) counted++
  }
  return cursor
}

/**
 * Signed working-day shift from `anchor`: offset=0 returns `anchor`
 * unchanged, offset>0 moves forward that many working days, offset<0
 * moves backward that many working days. Just addWorkingDays/
 * subtractWorkingDays picked by sign -- this is what a negative
 * dependency lag (successor allowed to start/finish BEFORE its
 * predecessor, i.e. an overlap) uses instead of only ever counting
 * forward.
 */
export function shiftWorkingDays(anchor: Date, offset: number, config: WorkingDaysConfig = DEFAULT_WORKING_DAYS): Date {
  if (offset === 0) return anchor
  if (offset > 0) return addWorkingDays(anchor, offset + 1, config)
  return subtractWorkingDays(anchor, Math.abs(offset) + 1, config)
}

/**
 * If `date` isn't a working day, snaps to whichever working day is
 * closer (ties go forward). Used for drag/resize snap-to-grid (spec
 * #10) -- dragging a task never leaves it starting/ending on a
 * configured non-working day.
 */
export function snapToNearestWorkingDay(date: Date, config: WorkingDaysConfig = DEFAULT_WORKING_DAYS): Date {
  if (isWorkingDay(date, config)) return date
  let forward = date
  for (let i = 0; i < 7; i++) {
    forward = addDays(forward, 1)
    if (isWorkingDay(forward, config)) break
  }
  let backward = date
  for (let i = 0; i < 7; i++) {
    backward = addDays(backward, -1)
    if (isWorkingDay(backward, config)) break
  }
  const forwardDelta = differenceInCalendarDays(forward, date)
  const backwardDelta = differenceInCalendarDays(date, backward)
  return forwardDelta <= backwardDelta ? forward : backward
}
